import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, Link, navigate, safeNext } from '../../app/router';
import { linkClass } from '../../ui';
import { AuthLayout, FormError } from './AuthLayout';
import { Spinner } from '../../ui';

/**
 * Landing page of magic links, OAuth and password-reset emails (PKCE): exchanges the one-time `code` for a
 * session, removes it from the address bar before anything else, then goes to `next`.
 */
export function AuthCallback() {
  const { t } = useTranslation();
  const api = useApi();
  const [params] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return { code: p.get('code'), next: safeNext(p.get('next')) };
  });
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // StrictMode runs effects twice; a code can be exchanged once.
    started.current = true;
    window.history.replaceState(null, '', window.location.pathname);
    if (!params.code) {
      setFailed(true);
      return;
    }
    api.auth.exchangeCode(params.code).then(
      () => navigate(params.next, { replace: true }),
      () => setFailed(true),
    );
  }, [api, params]);

  return (
    <AuthLayout title={t('auth.signInTitle')}>
      {failed ? (
        <>
          <FormError message={t('auth.callbackError')} />
          <Link className={linkClass} to={href('login')}>
            {t('auth.backToLogin')}
          </Link>
        </>
      ) : (
        <Spinner label={t('auth.loading')} className="text-fg-muted" />
      )}
    </AuthLayout>
  );
}
